
import JSZip from 'jszip';

import cornerstoneDICOMImageLoader from '@cornerstonejs/dicom-image-loader';
import { PlanarFreehandROITool } from '@cornerstonejs/tools';

import { showAlert } from "@/helpers/alerts";
import { t } from "@/i18n";
import { isRadiomicAnnotation } from "@/helpers/radiomics";

export async function getZipImageDicom(token, rmv_code) {
  const url_api = process.env.VUE_APP_API_URL + "/viewer";
  const api_url_endpoint_test_info = url_api + "/zip_image_dicom";

  try {
    const response = await fetch(api_url_endpoint_test_info, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: `rmv_code=${encodeURIComponent(rmv_code)}`
    });

    if (!response.ok) {
      console.log(`Error fetching zip: ${response.status}`);

      if (response.status === 401) {
        console.log(`Unauthorized user: ${response.status}`);
        window.location.replace(process.env.VUE_APP_WEBAPP_URL + "/account/login");
        return;
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    }

    console.log("Zip obtenido");

    // Obtener el contenido del ZIP como ArrayBuffer
    const zipArrayBuffer = await response.arrayBuffer();

    // Descomprimir con JSZip
    const zip = await JSZip.loadAsync(zipArrayBuffer);

    const dicomEntries = Object.values(zip.files).filter(file =>
      !file.dir && !file.name.startsWith('__MACOSX')
    );

    const blobs = await Promise.all(
      dicomEntries.map(entry => entry.async('arraybuffer')
        .then(buffer => new Blob([buffer], { type: 'application/dicom' }))
      )
    );

    // Registrar los blobs en cornerstone y devolver los imageIds
    const imageIds = blobs.map(blob =>
      cornerstoneDICOMImageLoader.wadouri.fileManager.add(blob)
    );

    return imageIds;

  } catch (error) {
    console.error('Error retrieving zip image dicom:', error);

    if (error.message.includes('Failed to fetch')) {
      showAlert('error', t('alert.server_connection_error'));
    } else {
      throw error;
    }
  }
}





/**
   * Function to create image IDs from DICOM metadata.
   * 
   * This function performs the following steps:
   * 1. Makes an API request to retrieve `StudyInstanceUID` and `SeriesInstanceUID`.
   * 2. Uses these UIDs to fetch metadata from another API endpoint.
   * 3. Extracts `SOPInstanceUID` and `SeriesInstanceUID` from the metadata.
   * 4. Generates `imageIds` in `wadors:` format for use with cornerstone.
   * 
   * @returns {Promise<Array<string>>} A promise resolving to an array of image IDs.
   */
export async function createImageIds(token, rmv_code) {
    const url_api = process.env.VUE_APP_API_URL+"/viewer";
    
    const SOP_INSTANCE_UID = '00080018';
    //const SERIES_INSTANCE_UID = '0020000E';
  
    let studyInstanceUID;
    let seriesInstanceUID;
    
    // Step 1: Fetch `StudyInstanceUID` and `SeriesInstanceUID`
    try {
      const api_url_endpoint_test_info = url_api+"/test_info";
  
      const response = await fetch(api_url_endpoint_test_info, {
        method: 'POST', // Using POST to send parameters in the body
        headers: {
          'Authorization': `Bearer ${token}`, // Token Bearer
          'Content-Type': 'application/x-www-form-urlencoded' // Request type
        },
        body: `rmv_code=${encodeURIComponent(rmv_code)}` // // Encode the parameter in the body
      });
  
      if (!response.ok) {
        console.log(`Error fetching test_info:: ${response.status}`);
  
        if (response.status === 401) {
          console.log(`Unauthorized user: ${response.status}`);
          window.location.replace(process.env.VUE_APP_WEBAPP_URL+"/account/login");  // Redirige y evita el botón de retroceso
          return;
        }else{
          throw new Error(`Error ${response.status}: ${response.statusText}`);    
        }
    
      }
      
      const data = await response.json();
      studyInstanceUID = data.StudyInstanceUID;
      seriesInstanceUID = data.SeriesInstanceUID;
  
      console.log(`Retrieved: StudyInstanceUID = ${studyInstanceUID}, SeriesInstanceUID = ${seriesInstanceUID}`);
    } catch (error) {
      console.error('Error retrieving StudyInstanceUID and SeriesInstanceUID:', error);
  
      if (error.message.includes('Failed to fetch')) {
        showAlert('error', t('alert.server_connection_error'));
      }else{
        throw error; // Stop execution on failure
      }
    }
    
    // Step 2: Fetch DICOM metadata using `StudyInstanceUID` and `SeriesInstanceUID`
    const studySearchOptions = {
      studyInstanceUID: studyInstanceUID,
      seriesInstanceUID: seriesInstanceUID,
    };
  
    const api_url_endpoint_metadata = `${url_api}/studies/${studySearchOptions.studyInstanceUID}/series/${studySearchOptions.seriesInstanceUID}/metadata`;
  
    const response = await fetch(api_url_endpoint_metadata, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'rmv_code': rmv_code, // Agregar el encabezado personalizado aquí
      },
    });
  
    if (!response.ok) {
      console.log("Request error. NOT FOUND");
      //throw new Error(`Error en la solicitud: ${response.statusText}`);
    }
  
    const instances = await response.json();
    if (process.env.VUE_APP_DEBUG === "true") {
      console.log("INSTANCES");
      console.log(instances);
    }
  
    // Step 3: Validate response format
    if (!Array.isArray(instances)) {
      console.error("Error in server response::", instances);
      throw new Error(instances.error || "Invalid server response.");
    }
  
    // Step 4: Generate image IDs for cornerstone
    let imageIds = instances.map((instanceMetaData) => {
      //const SeriesInstanceUID = instanceMetaData[SERIES_INSTANCE_UID].Value[0];
      const SOPInstanceUIDToUse = instanceMetaData[SOP_INSTANCE_UID].Value[0];
  
      const prefix = 'wadors:';
  
      const imageId =
        prefix +
        url_api +
        '/studies/' +
        studyInstanceUID +
        '/series/' +
        studySearchOptions.seriesInstanceUID +
        '/instances/' +
        SOPInstanceUIDToUse +
        '/frames/1';
  
      // Store metadata in cornerstone's metadata manager
      cornerstoneDICOMImageLoader.wadors.metaDataManager.add(
        imageId,
        instanceMetaData
      );
      return imageId;
    });
  
    return imageIds;
  }
  
  /**
   * Saves annotation data from a given viewport and sends it to the server.
   * 
   * @param {Object} view - The viewport object containing the Frame of Reference UID.
   */
  export function saveData(view, annotations, cornerstoneTools, styles, rmv_code, token, segmentMapJsonLoaded, btnSave ) {
    if (process.env.VUE_APP_DEBUG === "true") {
      console.log("saveData");
    }
    showLoadingGif();

    btnSave.disabled = true;  // Activa loading

    // Retrieve annotation data from Cornerstone
    var annotationsToSave = cornerstoneTools.annotation.state.getAnnotationManager().saveAnnotations(view.getFrameOfReferenceUID(), PlanarFreehandROITool.toolName);
    console.log("ANOTACIONES PARA GUARDAR");
    console.log(annotations);

    if (!annotationsToSave || annotationsToSave.length === 0) {
      showAlert('error', t('alert.no_annotations_to_save'));
      btnSave.disabled = false;  // Activa loading si hay error

      return;
    }

    if (process.env.VUE_APP_DEBUG === "true") {
      console.log("ANOT:", annotationsToSave);
    }
    
    // Organize annotations into predefined categories
    // var map = {
    //   classLiver: Array.from(annotations[0].annotationUIDs), 
    //   classAorta: Array.from(annotations[1].annotationUIDs), 
    //   classIVC: Array.from(annotations[2].annotationUIDs)
    // };

    // Construir dinámicamente el map de clases → annotationUIDs
    const classMap = {};
    try{
      for (const [name, data] of segmentMapJsonLoaded.entries()) {
        const id = data.id - 1;
        const key = `class${name.replace(/\s+/g, '')}`; // Ej: 'Vena porta' → 'classVenaporta'
        classMap[key] = annotations[id]?.annotationUIDs ? Array.from(annotations[id].annotationUIDs) : [];
      }
    } catch (error){
      console.error("Error mapping annotations to class", error);
      hideLoadingGif();
      showAlert('error', t('alert.save_prep_error'));
      hideLoadingGif();
      btnSave.disabled = false; 

      return;
    }
    
    // Initialize the array that will hold the reduced annotations
    let reducedAnnotations = [];
    try{
      // Iterate through all saved annotations
      for (let i = 0; i < annotationsToSave.length; i++) {
        const annotation = annotationsToSave[i];

        // Skip ROIs radiómicas pendientes: viven sólo en memoria de sesión y
        // viajan por el endpoint de radiomics, NO deben acabar en `Annotation`.
        if (isRadiomicAnnotation(annotation.annotationUID)) {
          continue;
        }

        // Check if the annotation has a valid polyline contour
        if (
          annotation.data &&
          annotation.data.contour &&
          annotation.data.contour.polyline
        ) {
          // Add only the relevant information to the final array
          reducedAnnotations.push({
            annotationUID: annotation.annotationUID,
            data: {
              contour: {
                polyline: annotation.data.contour.polyline, // Keep only polyline points
              },
            },
          });
        }
      }
    } catch (error){
      console.error("Error saving annotations contour.", error);
      hideLoadingGif();
      showAlert('error', t('alert.save_prep_error'));
      hideLoadingGif();
      btnSave.disabled = false; 

      return;
    }

    // Construct the JSON object to send
    let data;
    try {
      data = {classes: classMap, state: reducedAnnotations};
      data = JSON.stringify(data, function(_, v) { return v === undefined ? null : v; });

      if (process.env.VUE_APP_DEBUG === "true") {
        console.log("Final Data to Send:", data);
      }

      // Calculate the size of annotation data in bytes
      const sizeInBytes = new TextEncoder().encode(data).length;
      console.log("Annotation Data Size (bytes):", sizeInBytes);
    } catch (error) {
      console.error("Error preparing annotation payload:", error);
      hideLoadingGif();
      showAlert('error', t('alert.save_prep_error'));
      btnSave.disabled = false;
      return;
    }
  
    // Send data to the server
    const url_api = process.env.VUE_APP_API_URL;
    const api_url_endpoint_test_annotations = `${url_api}/annotations/${view.getFrameOfReferenceUID()}?rmv_code=${encodeURIComponent(rmv_code)}`;

    const Http = new XMLHttpRequest();
    Http.open('POST', api_url_endpoint_test_annotations);
    Http.setRequestHeader('Content-type', 'application/json; charset=UTF-8');
    Http.setRequestHeader('Authorization', `Bearer ${token}`);  // Aquí pasas el token

    
    Http.onerror = function () {
      console.error("Network error: Unable to connect to the server.");
      hideLoadingGif();
      showAlert('error', t('alert.save_network_error'));
      btnSave.disabled = false; 

    };

    Http.onloadend = function () {
      if (Http.status >= 200 && Http.status < 300) {
        console.log("Annotation data saved");
        fetchNumberOfVersions(view, view.getFrameOfReferenceUID(), annotations, cornerstoneTools, styles, false, segmentMapJsonLoaded);
        hideLoadingGif();
        showAlert('success', t('alert.save_success'));
        btnSave.disabled = false; 
      } else {
        if(Http.status == 401){
          console.log(`Unauthorized user: ${Http.status}`);
          window.location.replace(process.env.VUE_APP_WEBAPP_URL+"/account/login");  // Redirige y evita el botón de retroceso
          return;
        }
        console.error(`API error: ${Http.status} - ${Http.statusText}`);
        hideLoadingGif();
        showAlert('error', t('alert.save_failed', { status: Http.status }));
        btnSave.disabled = false; 
      }
    };
  
    Http.send(data);
  }

  /**
   * Loads annotation data from the server and restores it in the viewport.
   * 
   * @param {Object} view - The viewport object containing the Frame of Reference UID.
   */
  export function loadData(view, annotations, cornerstoneTools, styles) {
    if (process.env.VUE_APP_DEBUG === "true") {
      console.log("loadData");
    }
  
    // Define the API endpoint
    const url_api = process.env.VUE_APP_API_URL;
    const api_url_endpoint_test_annotations = url_api+"/annotations/" + view.getFrameOfReferenceUID();
  
    const Http = new XMLHttpRequest();
    Http.open('GET', api_url_endpoint_test_annotations);
  
    // Manejo de errores de red
    Http.onerror = function () {
      console.error("Network error: Unable to connect to the server.");
      showAlert('error', t('alert.load_network_error'));
    };

    Http.onloadend = function () {
      try {
        // Parse JSON
        var data = JSON.parse(Http.response, function(_, v) { return v === null ? undefined : v; });
    
        if (process.env.VUE_APP_DEBUG === "true") {
          console.log('Annotation data received');
          console.log(data);
        }
    
        // For each "annotation" received, we create an annotation with the minimal data, restore it, and add it to the corresponding segment
        data.state.forEach((annotation) => {
          // Create the minimal annotation object
          const annotationMinima = {
            annotationUID: annotation.annotationUID,
            data: {
              contour: {
                polyline: annotation.data.contour.polyline,
                closed: true   // Set to true to automatically close the contour
              },
              handles: {
                points: [],
              },
            },
            metadata: {
              FrameOfReferenceUID: view.getFrameOfReferenceUID(),    
              toolName: PlanarFreehandROITool.toolName,
              viewPlaneNormal: [-0, -0, -1],
            }
          }
    
          // Restore the annotation
          cornerstoneTools.annotation.state.addAnnotation(annotationMinima, view);
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log("Annotation restored ", annotationMinima);
          }
          // Assign the annotation to the corresponding class and apply the respective style
          if (data.classes.classLiver.includes(annotation.annotationUID)) {
            // Assign Liver class style
            cornerstoneTools.annotation.config.style.setAnnotationStyles(annotation.annotationUID, styles[0]);
            annotations[0].add(annotation.annotationUID);
            if (process.env.VUE_APP_DEBUG === "true") {
              console.log("Style set for class Liver");
            }
          } else if (data.classes.classAorta.includes(annotation.annotationUID)) {
            // Assign Aorta class style
            cornerstoneTools.annotation.config.style.setAnnotationStyles(annotation.annotationUID, styles[1]);
            annotations[1].add(annotation.annotationUID);
            if (process.env.VUE_APP_DEBUG === "true") {
              console.log("Style set for class Aorta");
            }
          } else if (data.classes.classIVC.includes(annotation.annotationUID)) {
            // Assign IVC class style
            cornerstoneTools.annotation.config.style.setAnnotationStyles(annotation.annotationUID, styles[2]);
            annotations[2].add(annotation.annotationUID);
            if (process.env.VUE_APP_DEBUG === "true") {
              console.log("Style set for class IVC");
            }
          }
        });

        hideLoadingGif();
        showAlert('success', t('alert.load_success'));

      }catch (error) {
        console.error("Error parsing or processing annotations:", error);
        hideLoadingGif();
        showAlert('error', t('alert.load_failed'));
      }
    }
  
    // Send the HTTP request
    Http.send();
  }

  /**
   * Fetches the number of available annotation versions from the server and creates a dropdown for selection.
   *
   * @param {Object} view - The viewport object where the annotations will be displayed.
   * @param {string} frameOfReferenceUID - The unique identifier for the frame of reference.
   */
  export async function fetchNumberOfVersions(view, framOfReferenceUID, annotations, cornerstoneTools, styles, loadVersion, segmentMapJsonLoaded) {
    try {
      
      // Fetch the number of available versions from the API
      const url_api = process.env.VUE_APP_API_URL;
  
      //Get the number of versions
      const response = await fetch(url_api+`/annotations/${framOfReferenceUID}/versions`);

      if (!response.ok) {
        // Manage especific errors
        if (response.status === 401) {
          console.error("Unauthorized user.");
          window.location.replace(process.env.VUE_APP_WEBAPP_URL + "/account/login");
          return;
        } else {
          showAlert('error', t('alert.versions_failed'));
        }
      }

      const data = await response.json();

      const numeroVersiones = data.number_of_versions;
      if (process.env.VUE_APP_DEBUG === "true") {
        console.log("Number of versions:", numeroVersiones);
      }
      // Get the container for the dropdown and clear any existing content
      const dropdownContainer = document.getElementById("versionDropdownContainer");
      dropdownContainer.innerHTML = ''; // Limpiar el contenedor antes de agregar las nuevas opciones
  
      // Select the dropdown element
      const select = document.createElement('select');
      select.id = 'versionDropdown';
  
      // Select the save ad load buttons
      const saveButton = document.getElementById("saveAnnotations");
      const loadButton = document.getElementById("loadAnnotations");
      const deleteButton = document.getElementById("delete");

      if (numeroVersiones === 0){
        const option = document.createElement('option');
        option.value = '';
        option.text = t('dropdown.no_versions');
        option.disabled = true;
        option.selected = true;
        select.appendChild(option);
        // Append the dropdown to its container
        dropdownContainer.appendChild(select);
      }else{
        let latestVersion = numeroVersiones; // Store the latest version

        // Populate the dropdown with version options
        for (let i = 1; i <= numeroVersiones; i++) {
          const option = document.createElement('option');
          option.value = i;
          option.text = t('dropdown.version', { n: i });
          if (i === latestVersion) {
            option.selected = true; // Select the latest version by default
          }
          select.appendChild(option);
        }
    
        // Append the dropdown to its container
        dropdownContainer.appendChild(select);
    
        // Function to load annotations for a selected version
        const loadSelectedVersion = async (selectedVersion) => {
          console.log("Versión seleccionada:", selectedVersion);
          showLoadingGif();
          try {
            const annotationResponse = await fetch(`${url_api}/version/${framOfReferenceUID}/${selectedVersion}`);
            const annotationData = await annotationResponse.json();

            // Process the received annotations and update the visualization
            if (process.env.VUE_APP_DEBUG === "true") {
              console.log("Annotations for selected version:", annotationData);
            }

            loadAnnotationsFromVersion(view, annotationData, annotations, cornerstoneTools, styles, segmentMapJsonLoaded);

            // Enable/disable buttons based on version selection
            if (selectedVersion == latestVersion) {
              saveButton.disabled = false;
              loadButton.disabled = false;
              deleteButton.disabled = false;
            } else {
              saveButton.disabled = true;
              loadButton.disabled = true;
              deleteButton.disabled = true;
            }

            hideLoadingGif();
            showAlert('success', t('alert.version_load_success', { n: selectedVersion }));

          } catch (error) {
            console.error("Error fetching annotations for the selected version:", error);
            hideLoadingGif();
            showAlert('error', t('alert.version_load_failed', { n: selectedVersion }));
          }
        };

        // Add an event listener for when the user selects a version
        select.addEventListener('change', async (event) => {
          const selectedVersion = event.target.value;
          loadSelectedVersion(selectedVersion);
        });

        //First time versions dropdown is created load de last version of annotations
        if(loadVersion){
          loadSelectedVersion(latestVersion);
        }

      }
  
    } catch (error) {
      console.error("Error:", error);
      showAlert('error', t('alert.internal_error', { error }));

    }
  } 

  /**
   * Loads annotations from a selected version and restores them in the viewport.
   * 
   * @param {Object} view - The viewport where the annotations should be displayed.
   * @param {Object} annotationData - The annotation data received from the API.
   */
  export function loadAnnotationsFromVersion(view, annotationData, annotations, cornerstoneTools, styles, segmentMapJsonLoaded) {
    try {
      // Remove previous annotations from the viewport
      removePlanarFreehandROIAnnotations(view, cornerstoneTools);
  
      console.log("Annotation data received", annotationData);
      

      // Map of annotationUID → className
      const annotationClassMap = new Map();

      for (const [classNameRaw, annotationUIDs] of Object.entries(annotationData.classes)) {
        // Convert "classLiver" → "Liver"
        const className = classNameRaw.replace(/^class/, '');
        annotationUIDs.forEach(uid => {
          annotationClassMap.set(uid, className);
        });
      }

      // Iterate through each received annotation and restore it
      annotationData.state.forEach((annotation) => {
        console.log("ANOTACION EN EL BUCLE")
        console.log(annotation.annotationUID)
        const annotationUID = annotation.annotationUID;
        const className = annotationClassMap.get(annotationUID);
        if (!className) {
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log(`Clase no encontrada para la anotación ${annotationUID}`);
          }
          return;
        }
        
        console.log("SEGMENTMAP");
        console.log(segmentMapJsonLoaded);
        const segmentInfo = segmentMapJsonLoaded.get(className);
        if (!segmentInfo) {
          if (process.env.VUE_APP_DEBUG === "true") {
            console.warn(`Clase "${className}" no está en segmentMapJsonLoaded`);
          }
          return;
        }

        const segmentId = segmentInfo.id;
        const segmentStyle = segmentInfo.style;

        // Create the minimal annotation object
        const annotationMinima = {
          annotationUID: annotation.annotationUID,
          data: {
            contour: {
              polyline: annotation.data.contour.polyline,
              closed: true, // Set to true to automatically close the contour
            },
            handles: {
              points: [],
            }
          },
          metadata: {
            FrameOfReferenceUID: view.getFrameOfReferenceUID(),    
            toolName: PlanarFreehandROITool.toolName,
            viewPlaneNormal: [-0, -0, -1],
          },
        };
  
        // Restore the annotation
        cornerstoneTools.annotation.state.addAnnotation(annotationMinima, view);
  
        // Apply style and add into his group
        cornerstoneTools.annotation.config.style.setAnnotationStyles(annotationUID, segmentStyle);
        annotations[segmentId-1]?.add(annotationUID);

      });
  
      if (process.env.VUE_APP_DEBUG === "true") {
        console.log("All annotations from version loaded.");
        console.log(annotations)
      }
    } catch (error) {
      console.error("Error loading annotations:", error);
      showAlert('error', t('alert.internal_error', { error }));

    }
  
  }

  /**
   * Removes all PlanarFreehandROI annotations from a given viewport.
   *
   * @param {Object} view - The viewport object where annotations should be removed.
   */
  export function removePlanarFreehandROIAnnotations(view, cornerstoneTools) {
    try {
      // Retrieve the Frame of Reference UID for the given viewport
      const groupKey = view.getFrameOfReferenceUID();
      const annotationManager = cornerstoneTools.annotation.state.getAnnotationManager();
  
      // Get all annotations associated with this viewport
      const annotations = annotationManager.getAnnotations(groupKey);
      
      if (process.env.VUE_APP_DEBUG === "true") {
        console.log("Anotaciones en removePlanarFreehandROIAnnotations");
        console.log(annotations);
      }
  
      // Check if there are any PlanarFreehandROI annotations to remove
      if (annotations && annotations.PlanarFreehandROI) {
        // Clone the annotation array to avoid issues while modifying it
        const annotationsToRemove = annotations.PlanarFreehandROI.slice();
  
        // Iterate and remove each annotation
        for (let i = 0; i < annotationsToRemove.length; i++) {
          const annotation = annotationsToRemove[i];
          annotationManager.removeAnnotation(annotation.annotationUID);
          console.log(`Removed annotation with UID: ${annotation.annotationUID}`);
        }
      } else {
        console.log("No PlanarFreehandROI annotations found.");
      }
      if (process.env.VUE_APP_DEBUG === "true") {
        console.log("Anotaciones en final removePlanarFreehandROIAnnotations");
        console.log(annotations);
      }
    } catch (error) {
      console.error("Error removing annotations:", error);
    }
  }

  function showLoadingGif() {
    const gif = document.getElementById("loading-gif")
    if (gif) gif.style.display = "inline-block"
  }
  
  function hideLoadingGif() {
    const gif = document.getElementById("loading-gif")
    if (gif) gif.style.display = "none"
  }